import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const RecipientManager = ({ onRecipientUpdate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedRecipients, setSelectedRecipients] = useState(new Set());

  const mockRecipients = [
  {
    id: 1,
    name: "Sarah Mitchell",
    email: "sarah.mitchell@bookreview.com",
    outlet: "Literary Review Weekly",
    avatar: "https://images.unsplash.com/photo-1637562772116-e01cda44fce8",
    avatarAlt: "Professional headshot of woman with shoulder-length brown hair in navy blazer",
    status: "pending",
    lastContact: "2024-10-15",
    openRate: 85,
    responseRate: 12,
    specialty: "Fiction, Mystery"
  },
  {
    id: 2,
    name: "Marcus Rodriguez",
    email: "marcus@literarytimes.net",
    outlet: "The Literary Times",
    avatar: "https://images.unsplash.com/photo-1678282955795-200c1e18bc7d",
    avatarAlt: "Professional headshot of Hispanic man with short black hair in dark suit",
    status: "sent",
    lastContact: "2024-10-28",
    openRate: 92,
    responseRate: 18,
    specialty: "Non-fiction, Biography"
  },
  {
    id: 3,
    name: "Emily Chen",
    email: "emily.chen@bookblogcentral.com",
    outlet: "Book Blog Central",
    avatar: "https://images.unsplash.com/photo-1560859389-c4fb2bd88016",
    avatarAlt: "Professional headshot of Asian woman with long black hair in white blouse",
    status: "opened",
    lastContact: "2024-10-30",
    openRate: 78,
    responseRate: 15,
    specialty: "Young Adult, Romance"
  },
  {
    id: 4,
    name: "David Thompson",
    email: "d.thompson@podcastnetwork.com",
    outlet: "Author Interview Podcast",
    avatar: "https://images.unsplash.com/photo-1600630242764-41cf7d951ac4",
    avatarAlt: "Professional headshot of Caucasian man with beard wearing gray sweater",
    status: "replied",
    lastContact: "2024-10-29",
    openRate: 95,
    responseRate: 25,
    specialty: "Author Interviews, Podcasts"
  },
  {
    id: 5,
    name: "Lisa Park",
    email: "lisa@genrefocus.org",
    outlet: "Genre Focus Magazine",
    avatar: "https://images.unsplash.com/photo-1682917265735-e93b02a0532a",
    avatarAlt: "Professional headshot of Asian woman with short black hair in burgundy top",
    status: "bounced",
    lastContact: "2024-10-20",
    openRate: 0,
    responseRate: 0,
    specialty: "Science Fiction, Fantasy"
  }];


  const statusOptions = [
  { value: 'all', label: 'All Recipients' },
  { value: 'pending', label: 'Pending' },
  { value: 'sent', label: 'Sent' },
  { value: 'opened', label: 'Opened' },
  { value: 'replied', label: 'Replied' },
  { value: 'bounced', label: 'Bounced' }];


  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-warning text-warning-foreground',
      sent: 'bg-secondary text-secondary-foreground',
      opened: 'bg-accent text-accent-foreground',
      replied: 'bg-success text-success-foreground',
      bounced: 'bg-error text-error-foreground'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: 'Clock',
      sent: 'Send',
      opened: 'Eye',
      replied: 'MessageCircle',
      bounced: 'AlertTriangle'
    };
    return icons?.[status] || 'Circle';
  };

  const filteredRecipients = mockRecipients?.filter((recipient) => {
    const matchesSearch = recipient?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    recipient?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    recipient?.outlet?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesStatus = filterStatus === 'all' || recipient?.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const toggleRecipientSelection = (recipientId) => {
    const newSelected = new Set(selectedRecipients);
    if (newSelected?.has(recipientId)) {
      newSelected?.delete(recipientId);
    } else {
      newSelected?.add(recipientId);
    }
    setSelectedRecipients(newSelected);
    onRecipientUpdate?.(Array.from(newSelected));
  };

  const selectAll = () => {
    const allIds = new Set(filteredRecipients.map((r) => r.id));
    setSelectedRecipients(allIds);
    onRecipientUpdate?.(Array.from(allIds));
  };

  const deselectAll = () => {
    setSelectedRecipients(new Set());
    onRecipientUpdate?.([]);
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="border-b border-border p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-accent rounded-md flex items-center justify-center">
              <Icon name="Users" size={16} color="white" />
            </div>
            <h2 className="text-lg font-heading font-semibold text-foreground">Recipients</h2>
          </div>
          <div className="text-sm text-muted-foreground">
            {selectedRecipients?.size} of {filteredRecipients?.length} selected
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Search recipients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)} />

          </div>
          <Select
            options={statusOptions}
            value={filterStatus}
            onChange={setFilterStatus}
            placeholder="Filter by status" />

        </div>

        {/* Bulk Actions */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center space-x-2">
            <Button variant="ghost" size="sm" onClick={selectAll}>
              Select All
            </Button>
            <Button variant="ghost" size="sm" onClick={deselectAll}>
              Deselect All
            </Button>
          </div>
          {selectedRecipients?.size > 0 &&
          <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" iconName="Edit" iconPosition="left">
                Customize
              </Button>
              <Button variant="outline" size="sm" iconName="UserMinus" iconPosition="left">
                Remove
              </Button>
            </div>
          }
        </div>
      </div>
      {/* Recipients List */}
      <div className="max-h-96 overflow-y-auto">
        {filteredRecipients?.length === 0 ?
        <div className="text-center py-8">
            <Icon name="Search" size={32} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground">No recipients found matching your criteria</p>
          </div> :

        <div className="divide-y divide-border">
            {filteredRecipients?.map((recipient) =>
          <div
            key={recipient?.id}
            className={`p-4 hover:bg-muted transition-colors ${
            selectedRecipients?.has(recipient?.id) ? 'bg-muted' : ''}`
            }>

                <div className="flex items-start space-x-3">
                  {/* Checkbox */}
                  <input
                type="checkbox"
                checked={selectedRecipients?.has(recipient?.id)}
                onChange={() => toggleRecipientSelection(recipient?.id)}
                className="mt-1 h-4 w-4 text-primary border-border rounded focus:ring-ring" />


                  {/* Avatar */}
                  <img
                src={recipient?.avatar}
                alt={recipient?.avatarAlt}
                className="w-10 h-10 rounded-full object-cover" />


                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-foreground">{recipient?.name}</h4>
                        <p className="text-sm text-muted-foreground">{recipient?.email}</p>
                        <p className="text-xs text-muted-foreground">{recipient?.outlet}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(recipient?.status)}`}>
                          <Icon name={getStatusIcon(recipient?.status)} size={12} className="mr-1" />
                          {recipient?.status?.charAt(0)?.toUpperCase() + recipient?.status?.slice(1)}
                        </span>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
                      <span>Open Rate: {recipient?.openRate}%</span>
                      <span>Response Rate: {recipient?.responseRate}%</span>
                      <span>Last Contact: {new Date(recipient.lastContact)?.toLocaleDateString()}</span>
                    </div>

                    {/* Specialty */}
                    <div className="mt-2">
                      <span className="text-xs text-muted-foreground">Specializes in: </span>
                      <span className="text-xs text-foreground">{recipient?.specialty}</span>
                    </div>

                    {/* Actions */}
                    {selectedRecipients?.has(recipient?.id) &&
                <div className="flex items-center space-x-2 mt-3">
                        <Button variant="outline" size="sm" iconName="Edit" iconPosition="left">
                          Customize Message
                        </Button>
                        <Button variant="ghost" size="sm" iconName="Eye" iconPosition="left">
                          View Profile
                        </Button>
                      </div>
                }
                  </div>
                </div>
              </div>
          )}
          </div>
        }
      </div>
      {/* Footer Stats */}
      <div className="border-t border-border p-4 bg-muted">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-lg font-semibold text-foreground">{filteredRecipients?.length}</div>
            <div className="text-xs text-muted-foreground">Total Recipients</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-success">
              {Math.round(filteredRecipients?.reduce((acc, r) => acc + r?.openRate, 0) / filteredRecipients?.length || 0)}%
            </div>
            <div className="text-xs text-muted-foreground">Avg Open Rate</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-accent">
              {Math.round(filteredRecipients?.reduce((acc, r) => acc + r?.responseRate, 0) / filteredRecipients?.length || 0)}%
            </div>
            <div className="text-xs text-muted-foreground">Avg Response Rate</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-primary">${(selectedRecipients?.size * 0.02)?.toFixed(2)}</div>
            <div className="text-xs text-muted-foreground">Estimated Cost</div>
          </div>
        </div>
      </div>
    </div>);

};

export default RecipientManager;